// frontend/src/components/checklists/DkApprovalPanel.tsx
import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { usePutChecklistsByChecklistIdDkApprovalToggleMutation } from "../../../../../openapi";
import { getChecklistThunk } from "../checklistSlice";
import { IChecklistDkApproval } from "@mcapps/shared/schemas/checklistSchema";
import { ChecklistSummaryTable } from "../ChecklistSummaryTable";

export const DkApprovalPanel: React.FC = () => {
  const checklistMongoId = useAppSelector((state) => state.checklistReducer.checklist?._id);
  const dkApproval = useAppSelector(
    (state) => (state.checklistReducer.checklist?.doksam?.dkApproval ?? null) as IChecklistDkApproval | null,
  );

  const { checklist, checklistCategories, checklistItems } = useAppSelector((s) => s.checklistReducer);

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.globalReducer.user);

  const [confirmOpen, setConfirmOpen] = useState(false);

  // API hook (OpenAPI RTK Query)
  const [toggleDkApproval, toggleState] = usePutChecklistsByChecklistIdDkApprovalToggleMutation();

  const busy = toggleState.isLoading;

  const actorUpn = useMemo(() => {
    const email = (user?.email ?? "").trim().toLowerCase();
    const upn = email.split("@")[0] ?? "";
    return upn.trim();
  }, [user?.email]);

  const dkUpn = (dkApproval?.upn ?? "").trim().toLowerCase();

  const isDk = dkUpn.length > 0 && actorUpn.length > 0 && dkUpn === actorUpn;

  const approvedAt = dkApproval?.approvedAt ?? null;
  const isApproved = approvedAt != null && String(approvedAt).trim().length > 0;

  const approvedAtText = useMemo(() => {
    if (!approvedAt) {
      return "";
    }
    const d = new Date(approvedAt);
    if (Number.isNaN(d.getTime())) {
      return String(approvedAt);
    }
    return d.toLocaleString();
  }, [approvedAt]);

  const dkName = (dkApproval?.name ?? "").trim();
  const dkLabel = dkName || (dkUpn ? dkUpn : "—");

  const onClickToggle = () => {
    setConfirmOpen(true);
  };

  const onConfirm = async () => {
    if (!checklistMongoId) {
      return;
    }

    try {
      await toggleDkApproval({
        checklistId: checklistMongoId,
        body: {
          isApproved, // nuvarande state i UI (före toggle)
        },
      }).unwrap();
      dispatch(getChecklistThunk(checklistMongoId));

      setConfirmOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  if (!dkApproval || !dkUpn) {
    return null;
  }

  return (
    <>
      <Card w={350} shadow="sm" padding="md" radius="md" withBorder>
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Title order={6}>Disiplinkontroll (DK)</Title>

              {isApproved ? (
                <Badge color="green" leftSection={<i className="fa fa-check" />}>
                  Godkjent
                </Badge>
              ) : (
                <Badge color="gray" leftSection={<i className="fa fa-clock-o" />}>
                  Ikke godkjent
                </Badge>
              )}
            </Group>

            {isDk && (
              <Button
                size="xs"
                variant={isApproved ? "light" : "filled"}
                loading={busy}
                leftSection={<i className={isApproved ? "fa fa-undo" : "fa fa-check"} />}
                onClick={onClickToggle}
              >
                {isApproved ? "Angre godkjenning" : "Godkjenn"}
              </Button>
            )}
          </Group>

          <Group gap="xs">
            <Text size="sm" c="dimmed">
              DK:
            </Text>
            <Text size="sm">{dkLabel}</Text>
            {dkUpn && (
              <Text size="sm" c="dimmed">
                ({dkUpn}@multiconsult.no)
              </Text>
            )}
          </Group>

          {isApproved && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Godkjent:
              </Text>
              <Text size="sm">{approvedAtText}</Text>
            </Group>
          )}

          {!isApproved && (
            <Text size="sm" c="dimmed">
              Sjekklisten er ikke godkjent av DK ennå.
            </Text>
          )}
        </Stack>
      </Card>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={isApproved ? "Angre DK-godkjenning?" : "Godkjenne sjekklisten?"}
        centered
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm">
            {isApproved
              ? "Er du sikker på at du vil angre DK-godkjenningen? Dette fjerner godkjenningsdatoen."
              : "Er du sikker på at du vil godkjenne sjekklisten som DK? Dette blir registrert med dato och navn."}
          </Text>

          <ChecklistSummaryTable items={checklistItems} checklist={checklist} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Avbryt
            </Button>
            <Button onClick={onConfirm} loading={busy} leftSection={<i className="fa fa-check" />}>
              Bekreft
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default DkApprovalPanel;
